import Vue from 'vue';
import {Component} from 'vue-property-decorator';
import {Location, NavigationGuardNext, Route} from 'vue-router';
import {HyroxWhiteboardPage, MssHyrox, MssHyroxMainPart, MssWorkout, WorkoutTypes} from '@/types/workouts';
import {PAGES} from '@/types/router';
import MssCarouselPreviewComponent from '@/components/mss-carousel-preview/mss-carousel-preview.component.vue';
import InfoTagComponent from '@/components/info-tag/info-tag.component.vue';
import DetailDescriptionComponent from '@/components/detail-description/detail-description.component.vue';
import TrainingPreviewComponent from '@/components/training-preview/training-preview.component.vue';
import workoutPreviewManager from '@/store/managers/workout-preview.manager';
import router from '@/router';
import {appModule} from '@/store/modules/app.module';
import {mssWorkoutsHyroxModule} from '@/store/modules/mss-workouts-hyrox.module';
import {mssWorkoutHyroxDescriptionModule} from '@/store/modules/mss-workouts-hyrox-description.module';
import {ctrlCmdModule} from "@/store/modules/control-commands.module";
import {CTRL_COMMAND_FEEDBACK} from "@/types/socket";

@Component({
  components: {
    'mss-carousel-preview-component': MssCarouselPreviewComponent,
    'info-tag-component': InfoTagComponent,
    'workout-details-component': DetailDescriptionComponent,
    'training-preview-component': TrainingPreviewComponent
  },
  beforeRouteEnter: (to: Route, from: Route, next: NavigationGuardNext<Vue>) =>
  {
    const id = to.params.id;
    let activeWorkout: MssHyrox;
    if (to.name == PAGES.VELOCITY_WORKOUT_DESCRIPTION) {
      activeWorkout = mssWorkoutsHyroxModule.velocityWorkouts.find(activity => activity.id === id) as MssHyrox;
    } else {
      activeWorkout = mssWorkoutsHyroxModule.workouts.find(activity => activity.id === id) as MssHyrox;
    }
    if (activeWorkout != undefined) {
      mssWorkoutHyroxDescriptionModule.init(activeWorkout);
      next();
    } else {
      router.back();
    }
  },
  beforeRouteLeave: (to: Route, from: Route, next: NavigationGuardNext<Vue>) =>
  {
    const {whiteboardPage, previewMode} = mssWorkoutHyroxDescriptionModule;
    // the workout start takes over the screens, so only stop the preview when leaving elsewhere
    const startsTheWorkout = [PAGES.MSS_HYROX_ACTIVE, PAGES.VELOCITY_WORKOUT_ACTIVE].includes(to.name as PAGES);
    if (!startsTheWorkout && (previewMode || whiteboardPage >= 0)) {
      const workoutType = from.name == PAGES.VELOCITY_WORKOUT_DESCRIPTION ? 'mss-velocity' : 'mss-hyrox';
      workoutPreviewManager.stop(from.params.id, workoutType, 0, 0);
    }
    next();
  }
})
export default class MssHyroxDescriptionComponent extends Vue
{

  private whiteboardCmdWatch?: CallableFunction

  public regularWorkoutsLocation = {name: PAGES.REGULAR_WORKOUTS} as Location;

  public workoutPreviewRoundIdx?: number;

  public workoutPreviewPartIdx = 0;

  get id()
  {
    return this.$route.params.id;
  }

  get activeWorkoutLocation()
  {
    return {
      name: this.workout.type === WorkoutTypes.VELOCITY ?
        PAGES.VELOCITY_WORKOUT_ACTIVE :
        PAGES.MSS_HYROX_ACTIVE,
      params: {id: this.id}
    } as Location;
  }

  get workout()
  {
    return mssWorkoutHyroxDescriptionModule.workout as MssHyrox;
  }

  get workoutName()
  {
    return this.workout?.name;
  }

  get workoutType()
  {
    return this.workout?.type === WorkoutTypes.VELOCITY ? 'mss-velocity' : 'mss-hyrox';
  }

  get tags()
  {
    return mssWorkoutHyroxDescriptionModule.tags.slice(0, 8);
  }

  get workoutDescription()
  {
    return mssWorkoutHyroxDescriptionModule.workoutDescription;
  }

  get workoutPreviewSlidesData()
  {
    return mssWorkoutHyroxDescriptionModule.workoutPreviewSlidesData;
  }

  get coverImageUrl()
  {
    return mssWorkoutHyroxDescriptionModule.coverImageUrl;
  }

  get equipmentTags()
  {
    return mssWorkoutHyroxDescriptionModule.equipmentTags;
  }

  get fitnessTypeTag()
  {
    return mssWorkoutHyroxDescriptionModule.fitnessTypeTag;
  }

  get previewIcon()
  {
    return this.isPreviewActive ? "theme-style timer-btn stop-icon icon-stop" : "theme-style timer-btn triangle-icon icon-tirangle-arrow-right";
  }

  get mainParts()
  {
    return this.workout?.mainParts;
  }

  get isItTheMainPart()
  {
    return this.workoutPreviewPartIdx >= this.workout?.warming_ups.length &&
      this.workoutPreviewPartIdx < this.workout?.warming_ups.length + this.mainParts?.length;
  }

  get isPreviewActive()
  {
    return mssWorkoutHyroxDescriptionModule.previewMode;
  }

  get isWhiteboardModeActive()
  {
    return this.whiteboardPageNumber >= 0;
  }

  get whiteboardPageNumber()
  {
    return mssWorkoutHyroxDescriptionModule.whiteboardPage;
  }

  get whiteboardPagesTotal()
  {
    return mssWorkoutHyroxDescriptionModule.whiteboardPagesTotal;
  }

  created()
  {
    if (this.workout != null) {
      this.workoutPreviewPartIdx = this.workout?.warming_ups.length;
      this.workoutPreviewRoundIdx = 0;
    }
  }

  swipeWhiteboardPage(page: number)
  {
    mssWorkoutHyroxDescriptionModule.swipeWhiteboardPage(page);
    if (this.whiteboardPagesTotal < 0) {
      typeof this.whiteboardCmdWatch === 'function' ? this.whiteboardCmdWatch() : 0
      this.whiteboardCmdWatch = ctrlCmdModule.$watch(module => module.hyroxWhiteboardCmd,
        cmd =>
        {
          if (cmd.event == CTRL_COMMAND_FEEDBACK.HYROX_WHITEBOARD_ACTIVATED && Number.isInteger(cmd.value.pages)) {

            if (Number.isInteger(cmd.value.pages)) {
              mssWorkoutHyroxDescriptionModule.changeWhiteboardTotalPages(cmd.value.pages as number);
              if (cmd.value.pages as number < 1) {
                this.onPreviewUpdate({
                  workoutPartIdx: this.workoutPreviewPartIdx,
                  roundIdx: this.workoutPreviewRoundIdx
                });
              }
            }

          }
        });
      workoutPreviewManager.showWhiteboard(this.id, this.workoutType);
      setTimeout(() => typeof this.whiteboardCmdWatch === 'function' ? this.whiteboardCmdWatch() : 0, 1000 * 5);
    } else if (page < 0) {
      workoutPreviewManager.hideWhiteboard(this.id, this.workoutType);
      mssWorkoutHyroxDescriptionModule.changeWhiteboardTotalPages(-1);
      mssWorkoutHyroxDescriptionModule.swipeWhiteboardPage(-1);
    } else {
      this.onPreviewUpdate({
        workoutPartIdx: this.workoutPreviewPartIdx,
        roundIdx: this.workoutPreviewRoundIdx
      });
    }
  }

  togglePreviewMode()
  {
    mssWorkoutHyroxDescriptionModule.togglePreviewMode();
    if (this.id != undefined) {
      if (mssWorkoutHyroxDescriptionModule.previewMode) {
        workoutPreviewManager.start(this.id, this.workoutType,
          this.workoutPreviewPartIdx, this.workoutPreviewRoundIdx);
      } else {
        workoutPreviewManager.stop(this.id, this.workoutType,
          this.workoutPreviewPartIdx, this.workoutPreviewRoundIdx);
      }
      appModule.getPreview(this.isPreviewActive);
    }
  }

  toggleHyroxWhiteboard()
  {
    this.swipeWhiteboardPage(this.whiteboardPageNumber < 0 ? 0 : -1);
    appModule.getPreview(this.isWhiteboardModeActive);
  }

  onPreviewUpdate(d: {workoutPartIdx: number; roundIdx?: number;})
  {
    const {workoutPartIdx, roundIdx} = d;

    const {isWhiteboardModeActive, isPreviewActive, isItTheMainPart} = this;
    if (this.workout?.id) {
      if (isWhiteboardModeActive) {

        if (this.whiteboardPageNumber < this.whiteboardPagesTotal) {
          workoutPreviewManager.showWhiteboardPage(
            this.workout.id,
            this.whiteboardPageNumber,
            this.workoutType);
        }
        else if (isItTheMainPart && this.whiteboardPageNumber == this.whiteboardPagesTotal) {
          const partIdx = (workoutPartIdx - this.workout.warming_ups.length);
          const coachTips = (this.mainParts.at(partIdx) as MssHyroxMainPart).zones.map((zone) =>
          {
            return zone.stations.map(station =>
            {
              return {position: station.position_seq + 1, text: station.description};
            });
          }).reduce((tips, update) =>
          {
            return tips.concat(update);
          }, []).map((coachTip) =>
          {
            return coachTip.text != '' ? coachTip : null;
          }).filter(coachTip => coachTip != null) as Array<{position: number; text: string;}>;
          workoutPreviewManager.showWhiteboardCoachtips(
            this.workout.id, workoutPartIdx, 
            this.workoutType, coachTips);
        }

      } else if (isPreviewActive) {
        if (workoutPartIdx != this.workoutPreviewPartIdx) {
          workoutPreviewManager.changeWorkoutPart(this.workout.id, this.workoutType,
            workoutPartIdx, roundIdx);
        } else if (roundIdx != undefined && roundIdx != this.workoutPreviewRoundIdx) {
          workoutPreviewManager.changeRound(this.workout.id, this.workoutType,
            workoutPartIdx, roundIdx);
        }
      }
    }

    this.workoutPreviewPartIdx = workoutPartIdx;
    this.workoutPreviewRoundIdx = roundIdx;
  }

  returnBack()
  {
    return this.$router.back();
  }

  beforeDestroy()
  {
    if (this.isPreviewActive) mssWorkoutHyroxDescriptionModule.togglePreviewMode();
    if (this.whiteboardPageNumber >= 0) {
      workoutPreviewManager.hideWhiteboard(this.id, this.workoutType);
      mssWorkoutHyroxDescriptionModule.swipeWhiteboardPage(-1);
    }
    mssWorkoutHyroxDescriptionModule.changeWhiteboardTotalPages(-1);
    appModule.getPreview(false);
  }
}